import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

declare global {
  interface Window {
    posthog: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private posthogInitialized = false;

  constructor() {
    this.initializePostHog();
  }

  private initializePostHog() {
    if (typeof window !== 'undefined' && !this.posthogInitialized) {
      try {
        // PostHog is already initialized via the HTML snippet
        // Just check if it's available and mark as initialized
        if (window.posthog) {
          this.posthogInitialized = true;
          console.log('[Analytics] PostHog detected and ready');
        } else {
          console.warn('[Analytics] PostHog not available yet');
        }
      } catch (error) {
        console.error('[Analytics] Failed to initialize PostHog:', error);
      }
    }
  }

  startTrackerWithId(id: string) {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', id);
    }
  }

  trackView(pageUrl: string, screenName: string) {
    // Track with web analytics (gtag) - this works reliably
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', {
        page_path: pageUrl,
        screen_name: screenName,
      });
    }

    // Track with PostHog
    if (this.posthogInitialized && window.posthog) {
      window.posthog.capture('$pageview', {
        $current_url: pageUrl,
        screen_name: screenName,
        platform: Capacitor.isNativePlatform() ? 'mobile' : 'web'
      });
    }

    // Track with native Firebase Analytics if available
    if (Capacitor.isNativePlatform()) {
      this.trackNativeView(pageUrl, screenName);
    }
  }

  private async trackNativeView(pageUrl: string, screenName: string) {
    try {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
      
      // Log a custom event for page view tracking
      await FirebaseAnalytics.logEvent({
        name: 'page_view',
        params: {
          page_path: pageUrl,
          screen_name: screenName,
          platform: 'ios'
        }
      });
      
      console.log(`[Analytics] Native page view tracked: ${screenName}`, { pageUrl });
    } catch (error) {
      console.warn('Native Firebase Analytics page tracking not available:', error);
    }
  }

  trackEvent(action: string, category: string, label?: string) {
    // Track with web analytics (gtag) - this works reliably
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', action, {
        event_category: category,
        event_label: label,
      });
    }

    // Track with PostHog
    if (this.posthogInitialized && window.posthog) {
      window.posthog.capture(action, {
        category: category,
        label: label || '',
        platform: Capacitor.isNativePlatform() ? 'mobile' : 'web'
      });
    }

    // Track with native Firebase Analytics if available
    if (Capacitor.isNativePlatform()) {
      this.trackNativeEvent(action, category, label);
    }
  }

  private async trackNativeEvent(action: string, category: string, label?: string) {
    try {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
      
      await FirebaseAnalytics.logEvent({
        name: action,
        params: {
          category: category,
          label: label || '',
          platform: 'ios'
        }
      });
      console.log(`[Analytics] Native event tracked: ${action}`, { category, label });
    } catch (error) {
      console.warn('Native Firebase Analytics not available:', error);
    }
  }

  // Set user ID for better tracking
  async setUserId(userId: string) {
    // Set user ID in PostHog
    if (this.posthogInitialized && window.posthog) {
      window.posthog.identify(userId);
      console.log(`[Analytics] PostHog user ID set: ${userId}`);
    }

    // Set user ID in native Firebase Analytics if available
    if (Capacitor.isNativePlatform()) {
      try {
        const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
        await FirebaseAnalytics.setUserId({ userId });
        console.log(`[Analytics] Firebase user ID set: ${userId}`);
      } catch (error) {
        console.warn('Could not set Firebase user ID:', error);
      }
    }
  }

  // Test method to verify analytics is working
  async testAnalytics() {
    console.log('[Analytics] Testing analytics...');
    this.trackEvent('test_event', 'debug', 'analytics_test');
    
    // Test PostHog
    if (this.posthogInitialized && window.posthog) {
      window.posthog.capture('test_posthog_event', {
        test_param: 'test_value',
        timestamp: Date.now().toString(),
        platform: Capacitor.isNativePlatform() ? 'mobile' : 'web'
      });
      console.log('[Analytics] PostHog test event sent successfully');
    }
    
    if (Capacitor.isNativePlatform()) {
      try {
        const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
        await FirebaseAnalytics.logEvent({
          name: 'test_native_event',
          params: {
            test_param: 'test_value',
            timestamp: Date.now().toString()
          }
        });
        console.log('[Analytics] Firebase test event sent successfully');
      } catch (error) {
        console.error('[Analytics] Firebase test failed:', error);
      }
    }
  }

  // Manual screen tracking for specific cases
  async trackScreen(screenName: string, additionalParams?: any) {
    console.log(`[Analytics] Manually tracking screen: ${screenName}`);
    
    // Track as a custom event
    this.trackEvent('screen_view', 'navigation', screenName);
    
    // Track as page view
    this.trackView(`/${screenName.toLowerCase().replace(' ', '_')}`, screenName);
    
    // Add any additional parameters
    if (additionalParams && Capacitor.isNativePlatform()) {
      try {
        const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
        await FirebaseAnalytics.logEvent({
          name: 'screen_view_custom',
          params: {
            screen_name: screenName,
            ...additionalParams,
            platform: 'ios'
          }
        });
      } catch (error) {
        console.warn('Could not track custom screen parameters:', error);
      }
    }
  }
}